import { LiveIndicator }   from '@/components/ui/LiveIndicator';
import { CortexLogoFull }  from '@/components/ui/CortexLogo';
import { formatClock }     from '@/lib/formatters';
import { RefreshCw, LogOut, ChevronDown } from 'lucide-react';
import { Link }            from 'react-router-dom';
import { cn }              from '@/lib/cn';
import { useAuth }         from '@/auth/AuthContext';
import { useState }        from 'react';
import type { SheetSource } from '@/config/sheets';

interface HeaderProps {
  siteName:   string;
  /** Timestamp of the LAST reading in the sheet (frozen at that time). */
  lastDataAt: Date | null;
  refreshing: boolean;
  onRefresh:  () => void;
}

export function Header({ siteName, lastDataAt, refreshing, onRefresh }: HeaderProps) {
  const { signOut, user, selectedSheet, setSelectedSheet } = useAuth();
  const [signingOut,   setSigningOut]   = useState(false);
  const [pickerOpen,   setPickerOpen]   = useState(false);

  const sheets      = user?.access.sheets ?? [];
  const multiMeter  = sheets.length > 1;

  // Show the timestamp of the LAST row in the sheet — frozen at that moment.
  // If the sheet stopped updating at 5 Jun 06:30, this stays at 5 Jun 06:30
  // instead of ticking with the wall clock.
  const displayTime = lastDataAt
    ? `${formatClock(lastDataAt)} IST`
    : '— · awaiting data';

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
  }

  function handlePick(sheet: SheetSource) {
    setSelectedSheet(sheet);
    setPickerOpen(false);
  }

  return (
    <header
      className="relative overflow-hidden pad-safe-top"
    >
      <div className="absolute inset-0 blob -z-10" />

      <div className="px-4 pb-3 pt-1">

        {/* ── Logo row ── */}
        <div className="relative flex items-center justify-center min-h-[56px]">

          {/* LEFT: Logout button */}
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            aria-label="Sign out"
            className={cn(
              'absolute left-0 w-9 h-9 grid place-items-center rounded-full',
              'bg-white/80 border border-ink-200/70 text-ink-600',
              'active:scale-95 transition shadow-card',
              signingOut && 'opacity-60',
            )}
          >
            <LogOut className="w-4 h-4" />
          </button>

          {/* CENTER: Logo + site name */}
          <Link
            to="/"
            className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
            aria-label="Dashboard"
          >
            <CortexLogoFull height={54} className="text-ink-900" />
            <span className="text-[17px] font-black tracking-tight text-ink-900 leading-none">
              {siteName}
            </span>
          </Link>

          {/* RIGHT: Refresh button */}
          <button
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh"
            className={cn(
              'absolute right-0 w-9 h-9 grid place-items-center rounded-full',
              'bg-white/80 border border-ink-200/70 text-ink-600',
              'active:scale-95 transition shadow-card',
              refreshing && 'opacity-60',
            )}
          >
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          </button>
        </div>

        {/* ── Meter picker (admin only — when multiple meters) ── */}
        {multiMeter && (
          <div className="mt-2 flex justify-center">
            <div className="relative">
              <button
                onClick={() => setPickerOpen(v => !v)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
                  'bg-white/80 border border-ink-200 text-ink-700 shadow-sm active:scale-95 transition',
                )}
              >
                <span className="max-w-[160px] truncate">{selectedSheet?.label ?? 'Select meter'}</span>
                <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', pickerOpen && 'rotate-180')} />
              </button>

              {pickerOpen && (
                <div className={cn(
                  'absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50',
                  'bg-white rounded-xl shadow-lg border border-ink-100 overflow-hidden min-w-[180px]',
                )}>
                  {sheets.map(sheet => (
                    <button
                      key={sheet.id}
                      onClick={() => handlePick(sheet)}
                      className={cn(
                        'w-full text-left px-4 py-2.5 text-xs font-medium transition-colors',
                        'hover:bg-brand-50 first:rounded-t-xl last:rounded-b-xl',
                        selectedSheet?.id === sheet.id
                          ? 'text-brand-700 bg-brand-50/60 font-semibold'
                          : 'text-ink-700',
                      )}
                    >
                      {sheet.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Last-data · live row ── */}
        <div className="mt-2 flex items-center justify-center gap-0">
          <span className="text-[12px] font-semibold text-ink-700">
            {displayTime}
          </span>
          <span className="mx-2 text-ink-300 font-bold">·</span>
          <LiveIndicator lastDataAt={lastDataAt} />
        </div>
      </div>
    </header>
  );
}